import { deserialize, DocumentObject, serialize } from '@jsonapiworld/jsonapi-serializer';
import { backendApiGateway, ErrorCodes, ErrorResponse } from '~/client/backend';
import {
  CompanyUser,
  OutstandingCustomFiltersData,
  UpdateCompanyUserRequest,
  UpdateOutstandingCustomFiltersRequest,
} from '~/types/company';
import { User } from '~/types/user';
import { isAxiosError } from '~/utils/helpers';
import {
  buildJsonAPIQueryParams,
  jsonAPIFieldsBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
} from '~/utils/jsonapi-query';

export const fetchCompanyUsers = async (companyId: string) => {
  const filter = jsonAPIFilterBuilder();
  filter.eq('company.id', companyId);
  const includeBuilder = jsonAPIIncludeBuilder(['user', 'userPersonalData']);
  const sortParams = buildJsonAPIQueryParams(filter, includeBuilder);
  const { data } = await backendApiGateway.get<DocumentObject>(`/api/v1/company-users`, {
    params: sortParams,
  });
  const deserialized = deserialize<CompanyUser>(data) as CompanyUser[];
  return deserialized;
};

export const fetchCurrentCompanyUser = async (companyId: string, userId: string) => {
  const filter = jsonAPIFilterBuilder();
  filter.eq('company.id', companyId);
  filter.eq('user.id', userId);
  const queryParams = buildJsonAPIQueryParams(filter);
  try {
    const { data } = await backendApiGateway.get<DocumentObject>('/api/v1/company-users', {
      params: queryParams,
    });
    const deserialized = deserialize<CompanyUser>(data) as CompanyUser[];
    return deserialized[0] ?? null;
  } catch (error) {
    if (
      isAxiosError<ErrorResponse>(error) &&
      error.response?.data.errors?.[0]?.code === ErrorCodes.NoActiveSubscription
    ) {
      return null;
    }
    throw error;
  }
};

export const patchCompanyUser = async (payload: UpdateCompanyUserRequest) => {
  const resourceData = serialize(payload, 'company-user');

  const { data } = await backendApiGateway.patch<DocumentObject>(`/api/v1/company-users/${payload.id}`, resourceData);

  return deserialize<CompanyUser>(data) as CompanyUser | null;
};

export const fetchOutstandingCustomFilters = async (companyId: string, userId: string) => {
  const filter = jsonAPIFilterBuilder();
  filter.eq('company.id', companyId);
  filter.eq('user.id', userId);
  const fieldsBuilder = jsonAPIFieldsBuilder([{ resource: 'company-user', fields: ['outstandingCustomFilters'] }]);
  const queryParams = buildJsonAPIQueryParams(filter, fieldsBuilder);
  const { data } = await backendApiGateway.get<DocumentObject>('/api/v1/company-users', { params: queryParams });
  const deserialized = deserialize<OutstandingCustomFiltersData>(data) as OutstandingCustomFiltersData[];
  return deserialized[0] ?? null;
};

export const patchOutstandingCustomFilters = async (payload: UpdateOutstandingCustomFiltersRequest) => {
  const resourceData = serialize(payload, 'company-user');

  const { data } = await backendApiGateway.patch<DocumentObject>(`/api/v1/company-users/${payload.id}`, resourceData);

  return deserialize<CompanyUser>(data) as CompanyUser | null;
};

export const deleteCompanyUser = async (id: CompanyUser['id']): Promise<void> => {
  await backendApiGateway.delete<DocumentObject>(`/api/v1/company-users/${id}`);
};

export const resendInvitation = async (companyUser: CompanyUser): Promise<User> => {
  await backendApiGateway.post(
    `/api/v1/company-users/${companyUser.id}/resend-invite`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  return companyUser.user;
};
